'use client';

import { FC, Fragment, useEffect, useState } from 'react';

import { FilterType, SelectedFilterType } from '@/shared/config';
import { Bullet, Filter } from '@/shared/ui';

import styles from './productFilters.module.scss';

const buildFilterRecord = (
  filters?: FilterType[],
  externalSelectedFilters?: SelectedFilterType[],
): Record<number, number[]> => {
  const initialFilterState: Record<number, number[]> = {};

  filters?.forEach((filter) => {
    initialFilterState[filter.attribute_id] = [];
  });

  if (externalSelectedFilters && externalSelectedFilters.length > 0) {
    externalSelectedFilters.forEach((filter) => {
      const attributeId =
        typeof filter.attribute_id === 'string'
          ? parseInt(filter.attribute_id, 10)
          : filter.attribute_id;

      initialFilterState[attributeId] = filter.values;
    });
  }

  return initialFilterState;
};

interface ProductFiltersProps {
  filters?: FilterType[];
  selectedFilters?: SelectedFilterType[];
  onAccept?: (value: Record<number, number[]>) => void;
}

const ProductFilters: FC<ProductFiltersProps> = (props) => {
  const { filters, selectedFilters: externalSelectedFilters, onAccept } = props;

  const [selectedFilters, setSelectedFilters] =
    useState<Record<number, number[]>>(() =>
      buildFilterRecord(filters, externalSelectedFilters),
    );
  useEffect(() => {
    setSelectedFilters(buildFilterRecord(filters, externalSelectedFilters));
  }, [filters, externalSelectedFilters]);

  const toggleFilter = (attributeId: number, value: number) => {
    setSelectedFilters((prev) => {
      const currentValues = [...(prev[attributeId] || [])];
      const valueIndex = currentValues.indexOf(value);
      const wasSelected = valueIndex !== -1;

      if (wasSelected) {
        currentValues.splice(valueIndex, 1);
      } else {
        currentValues.push(value);
      }

      const nextFilters = {
        ...prev,
        [attributeId]: currentValues,
      };

      if (wasSelected && currentValues.length === 0) {
        onAccept?.(nextFilters);
      }

      return nextFilters;
    });
  };

  const clearFilter = (attributeId: number) => {
    const newSelectedFilters = {
      ...selectedFilters,
      [attributeId]: [],
    };

    setSelectedFilters(newSelectedFilters);

    onAccept?.(newSelectedFilters);
  };

  const acceptFilter = () => {
    onAccept?.(selectedFilters);
  };

  return (
    <div className={styles.container}>
      <div className={styles.container__row}>
        {filters?.map((item) => (
          <Fragment key={`filter-${item.attribute_id}`}>
            <Filter
              disabled={!selectedFilters[item.attribute_id]?.length}
              label={item.attribute_name}
              options={item.values}
              value={selectedFilters[item.attribute_id]}
              onAccept={acceptFilter}
              onChange={(option) => toggleFilter(item.attribute_id, option)}
              onClear={() => clearFilter(item.attribute_id)}
            >
              {selectedFilters[item.attribute_id]?.length > 0 && (
                <Bullet
                  count={selectedFilters[item.attribute_id].length}
                  variant="border-black-24"
                />
              )}
            </Filter>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default ProductFilters;
